/**
 * SearchCommandPalette — global full-text search UI.
 *
 * Opens with Ctrl/Cmd+K (or by clicking the search icon in the header).
 * Searches across jobs, stores, products, and (for admins) users via
 * /api/services/search. Shows grouped results with icons and keyboard
 * navigation (arrow keys + Enter).
 *
 * Uses Postgres tsvector full-text search when available, falls back to
 * ILIKE/LIKE for SQLite dev. See src/app/api/services/search/route.ts.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useUiStore } from "@/store/ui";
import { useAuthStore } from "@/store/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Search,
  Package,
  Store as StoreIcon,
  Tag,
  User as UserIcon,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface SearchPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ResultGroup = {
  key: string;
  label: string;
  icon: typeof Package;
  items: Array<{
    id: string;
    title: string;
    subtitle: string;
    badge?: string;
    route: string;
    icon: typeof Package;
  }>;
};

export function SearchCommandPalette({ open, onOpenChange }: SearchPaletteProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced search — only search when query is 2+ chars.
  const debouncedQuery = useDebounce(query, 250);

  const { data, isLoading } = useQuery({
    queryKey: ["search", debouncedQuery],
    queryFn: () => api.search.query({ q: debouncedQuery, type: "all", limit: 8 }),
    enabled: debouncedQuery.length >= 2,
    staleTime: 10_000,
  });

  const results = data?.data?.results;

  // Flatten results for keyboard navigation.
  const flatItems = useCallback(() => {
    if (!results) return [];
    const items: Array<{ group: string; id: string; title: string; subtitle: string; route: string; icon: typeof Package }> = [];
    results.jobs?.forEach((j) =>
      items.push({
        group: "Jobs",
        id: j.id,
        title: j.code,
        subtitle: j.parcelDescription || j.pickupAddress,
        route: `app/jobs/${j.id}`,
        icon: Package,
      })
    );
    results.stores?.forEach((s) =>
      items.push({
        group: "Stores",
        id: s.id,
        title: s.name,
        subtitle: s.category,
        route: `app/stores/${s.id}`,
        icon: StoreIcon,
      })
    );
    results.products?.forEach((p) =>
      items.push({
        group: "Products",
        id: p.id,
        title: p.name,
        subtitle: p.store?.name || "",
        route: `app/stores/${p.storeId}`,
        icon: Tag,
      })
    );
    if (results.users?.length) {
      results.users.forEach((u) =>
        items.push({
          group: "Users",
          id: u.id,
          title: u.name,
          subtitle: u.email,
          route: "",
          icon: UserIcon,
        })
      );
    }
    return items;
  }, [results]);

  const items = flatItems();

  // Keyboard navigation
  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
      return;
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && items[activeIndex]) {
      e.preventDefault();
      selectItem(items[activeIndex]);
    }
  };

  function selectItem(item: (typeof items)[number]) {
    if (item.route) {
      navigate(item.route);
    }
    onOpenChange(false);
  }

  // Group items for display
  const groups: ResultGroup[] = [];
  if (results) {
    if (results.jobs?.length) {
      groups.push({
        key: "jobs",
        label: t("search.jobs") || "Jobs",
        icon: Package,
        items: results.jobs.map((j) => ({
          id: j.id,
          title: j.code,
          subtitle: j.parcelDescription?.slice(0, 60) || j.pickupAddress?.slice(0, 60),
          badge: j.status,
          route: `app/jobs/${j.id}`,
          icon: Package,
        })),
      });
    }
    if (results.stores?.length) {
      groups.push({
        key: "stores",
        label: t("search.stores") || "Stores",
        icon: StoreIcon,
        items: results.stores.map((s) => ({
          id: s.id,
          title: s.name,
          subtitle: s.category,
          badge: s.rating?.toFixed(1),
          route: `app/stores/${s.id}`,
          icon: StoreIcon,
        })),
      });
    }
    if (results.products?.length) {
      groups.push({
        key: "products",
        label: t("search.products") || "Products",
        icon: Tag,
        items: results.products.map((p) => ({
          id: p.id,
          title: p.name,
          subtitle: p.store?.name || "",
          badge: `${p.price} DJF`,
          route: `app/stores/${p.storeId}`,
          icon: Tag,
        })),
      });
    }
    if (results.users?.length && user?.isAdmin) {
      groups.push({
        key: "users",
        label: t("search.users") || "Users",
        icon: UserIcon,
        items: results.users.map((u) => ({
          id: u.id,
          title: u.name,
          subtitle: u.email,
          badge: u.activeRole,
          route: "",
          icon: UserIcon,
        })),
      });
    }
  }

  // Compute flat index offset per group for keyboard nav highlight
  let runningIndex = 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0" >
        <DialogHeader className="sr-only">
          <DialogTitle>{t("search.title") || "Search"}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center border-b px-4">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("search.placeholder") || "Search jobs, stores, products..."}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {query.length < 2 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t("search.minChars") || "Type at least 2 characters to search"}
            </div>
          ) : items.length === 0 && !isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t("search.noResults", { query }) || `No results for "${query}"`}
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="py-2">
                <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/30">
                  {group.label}
                </div>
                {group.items.map((item) => {
                  const idx = runningIndex++;
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={`${group.key}-${item.id}`}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => selectItem({ ...item, group: group.key } as any)}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${
                        isActive ? "bg-accent" : "hover:bg-accent/50"
                      }`}
                    >
                      <item.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        {item.subtitle && (
                          <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                        )}
                      </div>
                      {item.badge && (
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          {item.badge}
                        </Badge>
                      )}
                      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100" />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="border-t px-4 py-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            <kbd className="px-1.5 py-0.5 rounded border bg-muted">↑</kbd>{" "}
            <kbd className="px-1.5 py-0.5 rounded border bg-muted">↓</kbd>{" "}
            {t("search.toNavigate") || "to navigate"}
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded border bg-muted">↵</kbd>{" "}
            {t("search.toSelect") || "to select"}
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded border bg-muted">Esc</kbd>{" "}
            {t("search.toClose") || "to close"}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Debounce hook for search inputs. */
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * SearchTrigger — a button that opens the palette. Place in the app header.
 * Also registers the global Ctrl/Cmd+K keyboard shortcut.
 */
export function SearchTrigger() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted"
        aria-label={t("search.open") || "Open search"}
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">{t("search.placeholder") || "Search..."}</span>
        <kbd className="hidden sm:inline ml-2 px-1.5 py-0.5 rounded border bg-background text-xs">
          ⌘K
        </kbd>
      </button>
      <SearchCommandPalette open={open} onOpenChange={setOpen} />
    </>
  );
}
